const extensionFromMimeType = (mimeType) => {
  if (!mimeType || typeof mimeType !== "string") return null;

  const normalized = mimeType.toLowerCase().split(";")[0].trim();
  if (!normalized || normalized === "application/octet-stream") return null;

  const known = {
    "application/pdf": "pdf",
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/jpg": "jpg",
    "image/webp": "webp",
    "image/gif": "gif",
  };
  if (known[normalized]) return known[normalized];

  const subtype = normalized.split("/")[1] || "";
  if (!subtype) return null;
  if (subtype === "jpeg") return "jpg";
  return subtype.split("+")[0] || null;
};

export const getFilenameFromUrl = (
  url,
  fallbackName = "document",
  mimeType = null,
) => {
  if (!url) {
    if (fallbackName.includes(".")) return fallbackName;
    const ext = extensionFromMimeType(mimeType) || "pdf";
    return `${fallbackName}.${ext}`;
  }

  if (url.startsWith("data:")) {
    const mime = url.slice(5).split(";")[0] || "";
    const subtype = mime.split("/")[1] || "file";
    const ext = subtype === "jpeg" ? "jpg" : subtype.split("+")[0];
    return fallbackName.includes(".") ? fallbackName : `${fallbackName}.${ext}`;
  }

  try {
    const pathname = new URL(url, window.location.origin).pathname;
    const last = decodeURIComponent(
      pathname.split("/").filter(Boolean).pop() || "",
    );
    if (last && /\.[a-z0-9]+$/i.test(last)) {
      return last;
    }
  } catch {
    // Ignore invalid URLs and use the fallback name.
  }

  if (fallbackName.includes(".")) return fallbackName;

  const ext = extensionFromMimeType(mimeType) || "pdf";
  return `${fallbackName}.${ext}`;
};

const triggerDownload = (href, filename) => {
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadDocumentFile = async (url, filename) => {
  if (!url) return false;

  if (url.startsWith("data:") || url.startsWith("blob:")) {
    triggerDownload(url, getFilenameFromUrl(url, filename));
    return true;
  }

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch the file: ${response.status}`);
    }

    const blob = await response.blob();
    const resolvedFilename = getFilenameFromUrl(url, filename, blob.type);
    const blobUrl = window.URL.createObjectURL(blob);
    triggerDownload(blobUrl, resolvedFilename);
    window.URL.revokeObjectURL(blobUrl);
    return true;
  } catch (error) {
    console.error("Error downloading the file:", error);
    const resolvedFilename = getFilenameFromUrl(url, filename);
    const link = document.createElement("a");
    link.href = url;
    link.download = resolvedFilename;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return true;
  }
};
